// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "swiper/css/scrollbar";

import "../../../theme/SwiperOverrides.css";

import * as styles from "./SectionSignPostCardCarousel.module.scss";

import React, { useCallback, useState } from "react";
// import Swiper core and required modules
import { A11y, Navigation, Scrollbar } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { Pagination } from "../../../components/Pagination/Pagination";
import StandardSignPostCarouselCard from "../../../components/SignPostCards/StandardSignPostCarouselCard";
import TestimonialSignPostCarouselCard from "../../../components/SignPostCards/TestimonialSignPostCarouselCard";
import { Button } from "../../../components/common";
import { useClientSide } from "../../../hooks/useClientSide";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";

export interface ParagraphButtonProps {
	title: string;
	intro?: string;
	htmlIntro?: string;
	items: DogsTrust.SignPostCardProps[];
	theme?: "primary" | "alternate";
	testimonial?: boolean;
	isLastSection?: boolean;
	backgroundColour?: string;
	customBorder?: string;
	link?: DogsTrust.Link;
	jumpLinksTitle?: string;
}
export const SectionSignPostCardCarousel: React.FC<ParagraphButtonProps> = ({
	title,
	intro,
	htmlIntro,
	items,
	testimonial,
	customBorder = "",
	link,
	jumpLinksTitle,
	backgroundColour = Colours.SUNSHINE_YELLOW,
}) => {
	if (!items?.length) {
		// Adding noise to builds, turn on if needed
		// console.warn(
		//     `Hiding SectionSignPostCardCarousel: (${title}) - No items to display.`
		// );
		return null;
	}

	const [swiper, setSwiper] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const getBiggestCardTextCount = () => {
		const biggestCard = items.map((item) => {
			return (item.title?.length || 0) + (item.body?.length || 0);
		});
		return biggestCard.reduce((a, b) => Math.max(a, b), 0);
	};

	const prevSlide = () => {
		swiper?.slidePrev();
		setCurrentSlide((cur) => cur - 1);
	};
	const nextSlide = () => {
		swiper?.slideNext();
		setCurrentSlide((cur) => cur + 1);
	};
	const getIntro = useCallback(() => {
		if (intro?.length > 0)
			return (
				<div className={styles.introtext}>
					<p>{intro}</p>
				</div>
			);
		if (htmlIntro?.length > 0)
			return (
				<div
					className={styles.introtext}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
					dangerouslySetInnerHTML={{ __html: htmlIntro }}
				/>
			);
		return null;
	}, [intro, htmlIntro]);

	const isClientSide = useClientSide();

	const MAX_SLIDES = 3;

	// TODO make sure you can scroll to end
	return (
		<WrappedSection
			sectionName={"SectionSignPostCardCarousel"}
			customBorder={customBorder}
			sectionColour={backgroundColour}
		>
			<div className={styles.signpostcardcarouselinner}>
				<div className={styles.titlewrapper}>
					{jumpLinksTitle ? (
						<h2 data-jumplinkstitle={jumpLinksTitle} className={styles.title}>
							{title}
						</h2>
					) : (
						<h2 className={styles.title}>{title}</h2>
					)}
				</div>
				{getIntro()}
				<div className={styles.swiperwrapper}>
					{isClientSide && (
						<Swiper
							aria-label={"Carousel of cards"}
							className={styles.swiper}
							modules={[Navigation, A11y, Scrollbar]}
							edgeSwipeThreshold={10}
							threshold={0}
							grabCursor={true}
							longSwipes={true}
							resistance={false}
							shortSwipes={false}
							// loop cannot be configured in breakpoints, so we need to do it here
							loop={items.length > MAX_SLIDES}
							centeredSlides={false}
							onSwiper={(swiper) => setSwiper(swiper)}
							noSwiping={items.length <= MAX_SLIDES}
							// if the number of items is lower than 4, this bumps the swiper to the left & looks nicer
							initialSlide={items.length <= MAX_SLIDES ? 1 : 0}
							breakpoints={{
								320: {
									slidesPerView: 1.1,
									enabled: true,
								},
								375: {
									slidesPerView: 1.1,
									enabled: true,
								},
								414: {
									slidesPerView: 1.3,
									enabled: true,
								},
								500: {
									slidesPerView: 1.5,
									enabled: true,
								},
								600: {
									slidesPerView: 1.5,
									enabled: true,
								},
								700: {
									slidesPerView: 2.3,
									enabled: items.length >= MAX_SLIDES,
								},
								800: {
									slidesPerView: 2.2,
									enabled: items.length >= MAX_SLIDES,
								},
								900: {
									slidesPerView: 2.5,
									enabled: items.length >= MAX_SLIDES,
								},
								1000: {
									slidesPerView: MAX_SLIDES,
								},
							}}
						>
							{items.map((signPostCard) => (
								<SwiperSlide key={signPostCard.title}>
									{!testimonial ? (
										<StandardSignPostCarouselCard
											{...signPostCard}
											biggestSiblingTextCount={getBiggestCardTextCount()}
										/>
									) : (
										<TestimonialSignPostCarouselCard
											{...signPostCard}
											biggestSiblingTextCount={getBiggestCardTextCount()}
										/>
									)}
								</SwiperSlide>
							))}
						</Swiper>
					)}
					<div className={styles.pagination}>
						<Pagination
							onFirstClick={() => {
								swiper.slideTo(0);
								setCurrentSlide(0);
							}}
							onPreviousClick={prevSlide}
							onNextClick={nextSlide}
							onLastClick={() => {
								swiper.slideTo(items.length - MAX_SLIDES);
								setCurrentSlide(items.length - MAX_SLIDES);
							}}
							firstDisabled={currentSlide === 0}
							previousDisabled={currentSlide === 0}
							nextDisabled={currentSlide === items.length - MAX_SLIDES}
							lastDisabled={currentSlide === items.length - MAX_SLIDES}
							position={currentSlide}
							total={items.length}
							slim={true}
							noOffset={true}
						/>
					</div>
					{link && (
						<div className={styles.buttonwrapper}>
							<Button variant={"secondary"} url={link.url}>
								{link.title}
							</Button>
						</div>
					)}
				</div>
			</div>
		</WrappedSection>
	);
};

export default SectionSignPostCardCarousel;
